import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Reveal, Overline } from "../components/Reveal";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import { ArrowUpRight, MapPin } from "lucide-react";

export default function BeforeAfterPage() {
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/projects", { params: { limit: 10 } })
      .then((r) => setProjects(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const p = projects[active];

  return (
    <div>
      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-36 md:pt-44 pb-12">
        <Reveal>
          <Overline>Before / After</Overline>
          <h1 className="mt-6 font-serif font-light text-5xl md:text-7xl leading-[0.95] max-w-4xl">
            Real <em className="gold-gradient-text">transformations.</em>
          </h1>
          <p className="mt-8 max-w-2xl text-white/60 font-body text-lg leading-relaxed">
            The latest 10 projects we've completed across Virginia. Drag the gold handle
            on any project to reveal the transformation — from tired to timeless.
          </p>
        </Reveal>
      </section>

      {/* Feature slider */}
      {loading ? (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
          <div className="h-96 border border-white/10 flex items-center justify-center text-white/30">Loading…</div>
        </section>
      ) : !projects.length ? (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
          <div className="border border-white/10 p-12 text-center bg-[#0a0a0a]">
            <p className="text-white/60 font-body">Before & After projects will appear here soon.</p>
            <Link to="/contact" data-testid="ba-empty-cta" className="mt-8 inline-flex items-center gap-3 bg-gold text-black text-sm font-semibold uppercase tracking-widest px-8 py-4 hover:bg-white transition-colors duration-300">
              Request a Free Quote <ArrowUpRight size={18} />
            </Link>
          </div>
        </section>
      ) : (
        <>
          <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16">
            <Reveal>
              <BeforeAfterSlider key={p.id} before={p.before_image} after={p.after_image} beforeLabel="BEFORE" afterLabel="AFTER" />
              <div className="mt-8 flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                  <p className="text-xs uppercase tracking-widest text-gold mb-2">{p.category}</p>
                  <p className="font-serif text-3xl md:text-4xl">{p.title}</p>
                  {p.location && (
                    <p className="text-white/50 text-sm font-body mt-2 inline-flex items-center gap-1.5">
                      <MapPin size={13} /> {p.location}
                    </p>
                  )}
                  {p.description && (
                    <p className="mt-4 text-white/60 font-body max-w-xl">{p.description}</p>
                  )}
                </div>
                {projects.length > 1 && (
                  <div className="flex flex-wrap gap-2">
                    {projects.map((item, i) => (
                      <button
                        key={item.id}
                        data-testid={`ba-thumb-${i}`}
                        onClick={() => setActive(i)}
                        className={`h-16 w-24 overflow-hidden border transition-all ${i === active ? "border-gold" : "border-white/10 opacity-50 hover:opacity-100"}`}
                        aria-label={`View ${item.title}`}
                      >
                        <img src={item.after_image} alt={item.title} className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Reveal>
          </section>

          {/* Grid of all 10 — each one is its own interactive slider */}
          <section className="border-t border-white/10 bg-[#080808] py-24 md:py-32">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
              <Reveal>
                <Overline>Portfolio</Overline>
                <div className="mt-6 flex flex-wrap items-baseline justify-between gap-4">
                  <h2 className="font-serif font-light text-4xl md:text-5xl">Latest 10 projects</h2>
                  <p className="text-white/40 text-sm">Drag any slider — newest first</p>
                </div>
              </Reveal>
              <div className="mt-14 grid md:grid-cols-2 gap-6">
                {projects.map((pr, i) => (
                  <Reveal key={pr.id} delay={(i % 2) * 0.08}>
                    <div data-testid={`ba-card-${pr.id}`} className="border border-white/10 bg-[#0a0a0a] overflow-hidden group hover:border-gold/40 transition-colors">
                      <BeforeAfterSlider
                        key={pr.id}
                        before={pr.before_image}
                        after={pr.after_image}
                        beforeLabel="BEFORE"
                        afterLabel="AFTER"
                      />
                      <div className="p-5 flex flex-wrap items-baseline justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[10px] uppercase tracking-widest text-gold">{pr.category}</p>
                          <p className="font-serif text-xl md:text-2xl mt-1 truncate">{pr.title}</p>
                          {pr.location && (
                            <p className="text-white/40 text-sm inline-flex items-center gap-1.5 mt-1">
                              <MapPin size={12} /> {pr.location}
                            </p>
                          )}
                        </div>
                        <span className="text-[10px] uppercase tracking-widest text-white/40">#{i + 1} of 10</span>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
            <Reveal>
              <Overline className="justify-center">Ready?</Overline>
              <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl">Your home could be next.</h2>
              <Link
                data-testid="ba-cta"
                to="/contact"
                className="mt-8 inline-flex items-center gap-3 bg-gold text-black text-sm font-semibold uppercase tracking-widest px-8 py-4 hover:bg-white transition-colors duration-300"
              >
                Request a Free Quote <ArrowUpRight size={18} />
              </Link>
            </Reveal>
          </section>
        </>
      )}
    </div>
  );
}
